import moment from 'moment';
import millify from 'millify';
import {
  getPercents,
  getMaxValueForGraph,
  getSortedSourcesForDay,
  getDisplayName,
  isNetResellerRevenueSource,
} from './dataHelpers';
import {balloonConfig, getBalloonDateFormat} from './configs';
import {getChartBalloonShellStyles} from '../../../helpers/chartBalloonStyles';

// Safely format values for tooltips.
// Returns '-' when value is missing or invalid instead of throwing.
const formatTooltipValue = (value, disableMillify = false) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const numberValue = typeof value === 'number' ? value : Number(value);

  if (!Number.isFinite(numberValue)) {
    return '-';
  }

  if (disableMillify) {
    return numberValue.toLocaleString('en-US', {maximumFractionDigits: 2});
  }

  try {
    return millify(numberValue);
  } catch (e) {
    return '-';
  }
};

const metricPrefix = {
  graphData: '',
  graphDataS2S: 'Prebid Server ',
  graphDataNonS2S: 'Prebid Client ',
  graphDataDirect: 'Direct ',
  graphDataReseller: 'Reseller ',
};

export {getChartBalloonShellStyles};

export const createBalloonFunction = (
  item,
  demandData,
  metricsConfig,
  enabledMetric,
  sourcesConfig,
  disabledSources,
  groupingEnabled,
  dataProviderKey,
  sortOrder = 'desc',
  sortByKey = null,
  type = null,
  period = null,
  splitMode = 'serverClient',
  directSourcesSet = new Set(),
  netRevenueLabelContext = null,
  theme = null,
  globalDisableMillify = false
) => {
  let maxHeight = groupingEnabled ? 300 : 150;
  let minWidth = groupingEnabled ? 350 : 410;

  const balloonDateFormat = getBalloonDateFormat(period);
  const shellStyles = theme ? getChartBalloonShellStyles(theme) : null;
  const shellStyle = shellStyles?.shell ?? 'border-radius: 4px; padding: 8px; background: rgba(0,0,0,.8); color: #c7c7c7; font-family: Inter, sans-serif; font-size: 13px; line-height: 15px; margin-top: 7px; user-select: none;';
  const tableStyle = shellStyles?.table ?? 'width:100%; border: none!important; color: #c7c7c7; text-align: left; font-size: 13px; line-height: 15px; font-family: Inter; user-select: none;';
  const stickyRowStyle =
    shellStyles?.stickyRow ??
    'font-weight: bold; font-size: 11px; position: sticky; top: 0; z-index: 1;';
  const metricDividerStyle = shellStyles?.metricDivider ?? 'box-shadow: 0 1px 0 0 rgba(255,255,255,.5); z-index: 1;';

  let html = `<div style="${shellStyle}">`;
  html += `<div style="max-height: ${maxHeight}px; overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch;">`;
  html += `<table style="${tableStyle}">`;
  html +=
    `<tr style="${stickyRowStyle}"><td>` +
    moment(item.dataContext['date']).format(balloonDateFormat) +
    '</td></tr>';

  // Check both perSource and calculated for the enabled metric
  let enabledMetricConfigKey = Object.keys(metricsConfig.perSource).find(
    key => metricsConfig.perSource[key].dataField === enabledMetric
  );

  let enabledMetricConfig = metricsConfig.perSource[enabledMetricConfigKey];

  // If not found in perSource, check calculated metrics
  if (!enabledMetricConfig && metricsConfig.calculated) {
    const calculatedKey = Object.keys(metricsConfig.calculated).find(
      key => metricsConfig.calculated[key].dataField === enabledMetric
    );
    if (calculatedKey) {
      enabledMetricConfig = metricsConfig.calculated[calculatedKey];
    }
  }

  // Show enabled metric totals/averages (now also for analytics)
  if (!enabledMetricConfig.disableTotals) {
    const disableMillifyForMetric =
      globalDisableMillify || Boolean(enabledMetricConfig.disableMillify);

    html +=
      `<tr style="min-width: ${minWidth}px; font-size: 16px; ${metricDividerStyle}"><td style="font-weight: bold; padding-bottom: 6px;">` +
      (enabledMetricConfig.cumulative
        ? `${metricPrefix[dataProviderKey]}${enabledMetricConfig.displayName}`
        : `${metricPrefix[dataProviderKey]}Average ${enabledMetricConfig.displayName}`) +
      ' ' +
      '</td><td style="font-weight: bold; padding-bottom: 6px; ">' +
      formatTooltipValue(
        item.dataContext[`enabledSources_${enabledMetricConfig.dataField}`],
        disableMillifyForMetric
      ) +
      (enabledMetricConfig.symbol ?? '') +
      `${
        enabledMetricConfig.cumulative && !enabledMetricConfig.dayLevelOnly
          ? ` <td style="text-align: right; font-size: 13px; padding-bottom: 6px;">(${getPercents(
              item.dataContext[`enabledSources_${enabledMetricConfig.dataField}`],
              item.dataContext[enabledMetricConfig.dataField]
            )}%)</td>`
          : ''
      }` +
      '<td/></tr>';
  }

  // Day-level metrics are not broken down by source - total row is enough
  if (enabledMetricConfig.dayLevelOnly) {
    html += '</table></div></div>';
    return html;
  }

  // Get sorted sources for this specific day using helper function
  const sourcesWithValues = getSortedSourcesForDay(
    item.dataContext,
    demandData.availableSources,
    metricsConfig,
    enabledMetric,
    disabledSources,
    sortOrder,
    sortByKey,
    dataProviderKey,
    splitMode,
    directSourcesSet
  );

  const netBadgeHtml =
    '<span style="display: inline-block; color: #fff; background: #56cb80; padding: 2px 6px; border-radius: 4px; font-weight: bold; font-size: 10px; margin-left: 8px; text-transform: uppercase; vertical-align: middle;">NET REVENUE</span>';

  // Build HTML from sorted sources
  sourcesWithValues.forEach(({source, config, value, totalValue}) => {
    const disableMillifyForMetric = globalDisableMillify || Boolean(config.disableMillify);
    const showNetBadge =
      netRevenueLabelContext &&
      isNetResellerRevenueSource(
        source,
        netRevenueLabelContext.stackType,
        netRevenueLabelContext.directSourcesSet
      );
    html +=
      `<tr><td style="text-transform: capitalize; padding-top: 5px; padding-right: 15px; color: ${
        sourcesConfig[source].color
      }">
      ${
        sourcesConfig[source].logo
          ? `<img
                src=${sourcesConfig[source].logo}
                alt=${source}
                style="height: 16px;width: 16px; position: relative; top: 4px; margin-right: 5px;"
              />`
          : '<div style="display: inline-block; height: 16px;width: 16px; position: relative; top: 4px; margin-right: 5px;"></div>'
      }

         
      ${getDisplayName(source, sourcesConfig)}
       ${sourcesConfig[source].direct ? 'Direct' : ''}
       ${showNetBadge ? netBadgeHtml : ''}
      
          </td></td>` +
      `<td style="padding-top: 5px; text-align: right; font-weight: bold">${formatTooltipValue(
        value,
        disableMillifyForMetric
      )}${config.symbol ?? ''} 
      
    
      </td>

      <td style="padding-top: 5px; text-align: right;">
      ${config.cumulative && config.comparisonWithTotalEnabled ? `(${getPercents(value, totalValue)}%)` : ''}
      </td>
      </tr>`;
  });

  html += '</table></div></div>';

  return html;
};

export const CHART_GRID_COLOR = '#7979793b';

/** Matches StackedReportGraph categoryAxis.color (labels/ticks). */
export const getCategoryAxisGuideColor = theme => theme?.palette?.graphColors?.themeGray;

export const getValueAxisConfig = (
  metricsConfig,
  shouldGraphStick,
  theme,
  fixedScale,
  demandData,
  sourcesConfig,
  groupingEnabled,
  dataProviderKey
) => {
  let valueAxes = [
    ...Object.keys(metricsConfig.perSource).map(metric => ({
      ...metricsConfig.perSource[metric].graphValueAxisConfig,
      titleBold: false,
      // Grid is visible when the graph is not sticky
      gridAlpha: shouldGraphStick ? 0 : 1,
      gridColor: CHART_GRID_COLOR,
      titleColor: theme.palette.graphColors.themeGray,
      color: theme.palette.graphColors.themeGray,
      axisColor: theme.palette.graphColors.themeGray,
      minimum: 0,
      maximum: fixedScale
        ? getMaxValueForGraph(demandData.graphData, metricsConfig.perSource[metric], sourcesConfig)
        : undefined,
      title: groupingEnabled
        ? metricsConfig.perSource[metric].displayName
        : `${metricPrefix[dataProviderKey]}${metricsConfig.perSource[metric].displayName}`,
    })),
  ];

  return valueAxes;
};

export const getGraphsConfig = (
  demandData,
  sourcesConfig,
  theme,
  hoveredSource,
  disabledSources,
  enabledMetric,
  hideSeparateGraphs,
  fixedScale,
  balloonFunction,
  metricsConfig,
  reportType
) => {
  // Reverse the sources array for graph order only
  const reversedSources = [...demandData.availableSourcesSorted].reverse();
  const graphs = [];

  // Generate graphs for each metric in config
  Object.keys(metricsConfig.perSource).forEach(metricKey => {
    const metricConfig = metricsConfig.perSource[metricKey];
    const dataField = metricConfig.dataField;
    const graphType = metricConfig.graphType;
    const valueAxisId = metricConfig.graphValueAxisConfig.id;
    const metricHidden = !enabledMetric.includes(dataField);

    // Day-level metrics (same value for every source) — one series only, never stacked
    if (metricConfig.dayLevelOnly) {
      const dayLevelGraph = {
        id: `dayLevel_${dataField}`,
        type: graphType,
        clustered: false,
        valueAxis: valueAxisId,
        bullet: 'none',
        useLineColorForBulletBorder: true,
        lineColor:
          graphType === 'column' ? theme.palette.background.default : theme.palette.text.primary,
        fillColors:
          graphType === 'column'
            ? theme.palette.graphColors?.revenue || theme.palette.primary.main
            : metricConfig.fillArea
              ? theme.palette.text.primary
              : undefined,
        lineAlpha: 0.8,
        fillAlphas: graphType === 'column' ? 0.9 : metricConfig.fillArea ? 0.6 : undefined,
        lineThickness: graphType === 'column' ? 2 : 4,
        legendValueText: metricConfig.displayName,
        legendPeriodValueText: metricConfig.displayName,
        valueField: `enabledSources_${dataField}`,
        hidden: metricHidden,
        balloon: balloonConfig,
        balloonFunction: balloonFunction,
      };
      Object.keys(dayLevelGraph).forEach(key => {
        if (dayLevelGraph[key] === undefined) {
          delete dayLevelGraph[key];
        }
      });
      graphs.push(dayLevelGraph);
      return;
    }

    // For metrics that don't have a "Separate by source" toggle (cumulative metrics),
    // always show per-source graphs even if the user previously disabled separation.
    // Otherwise, respect the global hideSeparateGraphs flag.
    const shouldHideSeparateGraphs =
      metricConfig.cumulative && metricConfig.cumulative === true ? false : hideSeparateGraphs;

    // Generate per-source graphs (skip when "Separate by source" is off for this metric)
    if (!shouldHideSeparateGraphs) {
      reversedSources.forEach(source => {
        const graphConfig = {
          id: `${source} ${dataField}`,
          type: graphType,
          clustered: false,
          newStack: graphType === 'column' ? false : undefined,
          valueAxis: valueAxisId,
          bullet: 'none',
          useLineColorForBulletBorder: true,
          lineColor:
            graphType === 'column' ? theme.palette.background.default : sourcesConfig[source].color,
          fillColors:
            graphType === 'column'
              ? sourcesConfig[source].color
              : metricConfig.fillArea
                ? sourcesConfig[source].color
                : undefined,
          lineAlpha:
            !hoveredSource || hoveredSource === source
              ? graphType === 'column'
                ? 0.8
                : 0.8
              : graphType === 'column'
                ? 0.1
                : 0.1,
          fillAlphas:
            graphType === 'column'
              ? !hoveredSource || hoveredSource === source
                ? 0.9
                : 0.1
              : metricConfig.fillArea
                ? !hoveredSource || hoveredSource === source
                  ? 0.6
                  : 0.05
                : undefined,
          lineThickness: graphType === 'column' ? 2 : 4,
          dashLength:
            graphType === 'smoothedLine' && metricConfig.indicatorType === 'dashed-line'
              ? 4
              : undefined,
          legendValueText: metricConfig.displayName,
          legendPeriodValueText: metricConfig.displayName,
          valueField: `${source} ${dataField}`,
          hidden: disabledSources.includes(source) || metricHidden,
          balloon: balloonConfig,
          balloonFunction: balloonFunction,
        };

        // Remove undefined properties
        Object.keys(graphConfig).forEach(key => {
          if (graphConfig[key] === undefined) {
            delete graphConfig[key];
          }
        });

        graphs.push(graphConfig);
      });
    }

    // Add aggregate graph for non-cumulative metrics (like average eCPM).
    // When separate by source is disabled for non-analytics, show only this average line.
    const showAggregateLine =
      !metricConfig.hideIndicator ||
      (reportType !== 'analytics' && shouldHideSeparateGraphs);
    if (!metricConfig.cumulative && showAggregateLine) {
      const aggregateGraph = {
        type: 'smoothedLine',
        valueAxis: valueAxisId,
        bullet: 'none',
        useLineColorForBulletBorder: true,
        lineColor: theme.palette.text.primary,
        lineAlpha: 0.8,
        lineThickness: 7,
        legendValueText: metricConfig.displayName,
        legendPeriodValueText: metricConfig.displayName,
        valueField: `enabledSources_${dataField}`,
        hidden: metricHidden,
        balloon: balloonConfig,
        balloonFunction: balloonFunction,
      };
      graphs.push(aggregateGraph);
    }
  });

  return graphs;
};
